<template>
  <div id="users-create">
    <SidebarLayout3 :breadcrumb="breadcrumb" :pageTitle="pageTitle">
      <!-- SIDEBAR -->
      <template #sidebar>
        <Heading element="h2" class="sidebar-title">
          Ações da página
        </Heading>
        <div class="sidebar-buttons">
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            class="sidebar-buttons--save"
            @click="usersId === null ? createUser() : editUser()"
          >
            Salvar
          </Button>
          <Button
            layout="outline"
            class="sidebar-buttons--return"
            @click="$router.push({ path: '/usuarios' })"
          >
            Voltar sem salvar
          </Button>
          <Button
            v-if="!routeType"
            variation="error"
            class="sidebar-buttons--delete"
            @click.stop.prevent="deleteModal(userId)"
          >
            Excluir usuário
          </Button>
        </div>
      </template>

      <!-- FORMULÁRIO -->
      <template #content>
        <Heading :level="3" element="h3">Informações do usuário</Heading>
        <PmwFormInput
          v-model="form.email"
          :error="error.email"
          :showErrorFeedbackMessage="true"
          :required="true"
          label="E-mail"
          class="text-input--email large"
        />

        <!-- SEGUNDA LINHA -->
        <span class="form">
          <PmwFormInput
            v-model="form.login"
            :error="error.login"
            :showErrorFeedbackMessage="true"
            :required="true"
            label="Login"
            class="text-input--login large"
          />
          <PmwFormInput
            v-model="form.nome"
            :error="error.nome"
            :showErrorFeedbackMessage="true"
            :required="true"
            label="Nome"
            class="text-input large"
          />
          <!-- DDI -->
          <PmwFormSelect
            v-model="form.celularDDI"
            :options="ddiOptions"
            :search="true"
            :infoIcon="true"
            :infoText="'O DDI padrão é definido como do Brasil (+55)'"
            :multiple="false"
            label="País/Região"
            class="select--medium"
          />
          <!-- DDD -->
          <PmwFormInput
            v-model="celular.ddd"
            :hasMask="true"
            :mask="
              form.celularDDI && form.celularDDI.value !== '+55'
                ? '#####'
                : '##'
            "
            label="DDD"
            placeholder="00"
            class="text-input small"
          />
          <!-- NÚMERO DE TELEFONE -->
          <PmwFormInput
            v-model="celular.numero"
            :hasMask="true"
            :mask="
              form.celularDDI && form.celularDDI.value !== '+55'
                ? '############'
                : ['####-####', '#####-####']
            "
            label="Celular"
            placeholder="00000-0000"
            class="text-input medium"
          />
        </span>

        <!-- TERCEIRA LINHA -->
        <span class="form">
          <!-- DDI -->
          <PmwFormSelect
            v-model="form.telefoneDDI"
            :options="ddiOptions"
            :infoIcon="true"
            :infoText="'O DDI padrão é definido como do Brasil (+55)'"
            :search="true"
            :multiple="false"
            label="País/Região"
            class="select--medium"
          />
          <!-- DDD -->
          <PmwFormInput
            v-model="telefoneCentralReservas.ddd"
            :hasMask="true"
            :mask="
              form.telefoneDDI && form.telefoneDDI.value !== '+55'
                ? '#####'
                : '##'
            "
            label="DDD"
            placeholder="00"
            class="text-input small"
          />
          <!-- NÚMERO DE TELEFONE -->
          <PmwFormInput
            v-model="telefoneCentralReservas.numero"
            :hasMask="true"
            :mask="
              form.telefoneDDI && form.telefoneDDI.value !== '+55'
                ? '############'
                : ['####-####', '#####-####']
            "
            label="Telefone"
            placeholder="0000-0000"
            class="text-input medium"
          />
          <Paragraph fontSize="small" class="paragraph--phone">
            Telefone utilizado nos e-mails da Central de Reservas.<br />
            Se nenhum telefone for informado, será utilizado o do hotel.
          </Paragraph>
        </span>
        <InputCheckbox
          v-model="form.revendedor"
          :checked="form.revendedor"
          label="Esse usuário é um revendedor?"
          class="checkbox-input"
          @input="formatCheckbox"
        />
        <span v-if="form.revendedor === true" class="form">
          <CnpjInput
            :value="form.revendedorCNPJ"
            label="CNPJ do revendedor"
            id="cnpj"
            @input="form.revendedorCNPJ = $event"
          />
          <PmwFormInput
            v-model="form.revendedorSource"
            :maxlength="50"
            label="Source do revendedor"
            type="text"
            class="text-input large"
          />
          <PmwFormInput
            v-model="form.revendedorLimiteHorasCentralReservas"
            :sufix="'Horas'"
            placeholder=""
            label="Limite"
            type="number"
            class="text-input--limit"
          />
        </span>
        <hr class="horizontal-line" />
        <span class="form">
          <PmwFormSelect
            v-model="form.hoteis"
            :infoIcon="true"
            :toggleAllLabel="'Todos'"
            :multiple="true"
            :search="true"
            :options="hoteisOptions"
            infoText="Hotéis selecionados para este usuário. Caso nenhum esteja selecionado, serão considerados todos."
            label="Hotéis vinculados"
            class="select"
          />

          <PmwFormSelect
            v-model="form.grupos"
            :infoIcon="true"
            :toggleAllLabel="'Todos'"
            :multiple="true"
            :search="true"
            :tooltipMinWidth="179"
            :error="error.grupos"
            :showErrorFeedbackMessage="true"
            :required="true"
            :options="gruposOptions"
            infoText="O grupo define quais permissões o usuário terá dentro do sistema. Seu preenchimento é obrigatório."
            label="Grupos do usuário"
            class="select"
          />
        </span>
        <hr v-if="!routeType" class="horizontal-line" />

        <!-- NOVA SENHA -->
        <div v-if="!routeType">
          <Heading :level="3">Criar nova senha</Heading>
          <Paragraph fontSize="small" class="paragraph--password"
            >Será enviado um email para o usuário cadastrar sua
            senha.</Paragraph
          >
          <Button
            layout="outline"
            size="large"
            @click.stop.prevent="changeUserPassword()"
          >
            Criar senha
          </Button>
        </div>

        <hr v-if="!routeType" class="horizontal-line" />

        <!-- Outras informações do usuário -->
        <span v-if="!routeType" class="bottom-info">
          <div>
            <Paragraph class="bottom-info--title"> Usuário criado em</Paragraph>
            <Paragraph fontSize="small" class="bottom-info--date">
              {{ form.dataCadastro }}
            </Paragraph>
          </div>
          <div v-if="form.dataAlteracao !== null">
            <Paragraph class="bottom-info--title"
              >Data da última alteração</Paragraph
            >
            <Paragraph
              v-if="form.usuarioAlteracao === null"
              fontSize="small"
              class="bottom-info--date"
            >
              {{ form.dataAlteracao }}
            </Paragraph>
            <Paragraph v-else fontSize="small" class="bottom-info--date">
              {{ form.dataAlteracao }} -
              {{ form.usuarioAlteracao }}
            </Paragraph>
          </div>
          <div>
            <Paragraph class="bottom-info--title">Data expiração</Paragraph>
            <Paragraph fontSize="small" class="bottom-info--date">
              {{ form.dataExpiracao }}
            </Paragraph>
          </div>

          <!-- Reativar usuário -->
          <div v-if="form.expirado">
            <Paragraph class="bottom-info--expired"
              >Esse usuário está expirado</Paragraph
            >
            <Paragraph class="bottom-info--link">
              <Button layout="outline" @click="reactivateUser()">
                Reativar usuário
              </Button>
            </Paragraph>
          </div>
        </span>
        <hr class="horizontal-line" />

        <!-- FOOTER -->
        <span class="bottom-buttons">
          <Button
            layout="outline"
            class="bottom-buttons--return"
            @click="$router.push({ path: '/usuarios' })"
          >
            Voltar sem salvar
          </Button>
          <Button
            :disabled="disableSaveButton"
            :tooltipText="
              'Para salvar, preencha os campos obrigatórios sinalizados com *'
            "
            :popperStrategy="'auto'"
            class="bottom-buttons--save"
            @click="usersId === null ? createUser() : editUser()"
          >
            Salvar
          </Button>
        </span>
      </template>
    </SidebarLayout3>

    <!-- MODAL DELEÇÃO -->
    <AlertModal
      v-model="showDeleteModal"
      :buttonClose="true"
      title="Excluir usuário"
      @alert-modal-cancel="cancelDisable()"
      @alert-modal-confirm="disableUser()"
    >
      <template #text>
        Este usuário não conseguirá mais entrar na conta. <br />
        Tem certeza que quer fazer isso?
      </template>
    </AlertModal>
  </div>
</template>

<script>
/** Componentes da lib */
import AlertModal from '@solucoes/components-lib-tmp/src/components/patterns/AlertModal'
import Button from '@solucoes/components-lib-tmp/src/components/elements/Button'
import PmwFormSelect from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormSelect'
import Heading from '@solucoes/components-lib-tmp/src/components/elements/Heading'
import InputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/InputCheckbox'
import Paragraph from '@solucoes/components-lib-tmp/src/components/elements/Paragraph'
import PmwFormInput from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormInput'

/** Imports do projeto */
import SidebarLayout3 from '@/layouts/SidebarLayout3'
import ddi from '@/mixins/ddi.json'
import { formatDateHour } from '@/helpers/date'
import { parseISO } from 'date-fns'
import CnpjInput from '@/components/_base/patterns/CnpjInput'

export default {
  name: 'UsersCreate',
  components: {
    Button,
    PmwFormSelect,
    PmwFormInput,
    Heading,
    InputCheckbox,
    Paragraph,
    SidebarLayout3,
    AlertModal,
    CnpjInput
  },
  data() {
    return {
      breadcrumb: [
        {
          label: 'Configurações'
        },
        {
          label: 'Usuários',
          url: '/admin/v2/usuarios'
        },
        {
          label: 'Criar usuário'
        }
      ],
      /** Dados de telefone */
      telefoneCentralReservas: {
        numero: '',
        ddd: ''
      },
      /** Dados de celular */
      celular: {
        numero: '',
        ddd: ''
      },
      /** V-model de cadastro/edição */
      form: {
        dataCadastro: null,
        dataAlteracao: null,
        dataExpiracao: null,
        email: null,
        login: null,
        nome: null,
        celularDDI: null,
        telefoneDDI: null,
        revendedor: false,
        revendedorCNPJ: null,
        revendedorSource: null,
        revendedorLimiteHorasCentralReservas: null,
        hoteis: [],
        grupos: [],
        expirado: null
      },
      /** Opções de hoteis */
      hoteisOptions: [],
      /** Opções de grupos */
      gruposOptions: [],
      /** Opções de DDI */
      ddiOptions: ddi,
      /** Guarda tipo de página (Edição/Criação)  */
      routeType: '',
      /** Título da página  */
      pageTitle: null,
      /** Exibição da modal de deleção */
      showDeleteModal: false,
      /** Guarda Id para deleção */
      userDelete: null,
      /** Guarda Id do usuário */
      userId: null,
      /** Reativar usuário ao clicar em desfazer */
      undo: null,
      /** Controle de erro */
      error: {
        email: false,
        login: false,
        nome: false,
        grupos: false
      },
      userHours: null
    }
  },
  watch: {
    /** Controle de erro de login */
    'form.login': {
      handler: function() {
        if (this.form.login.length !== 0) {
          this.error.login = false
        } else if (this.form.login.length === 0) {
          this.error.login = true
        }
      },
      deep: true
    },

    /** Controle de erro de email */
    'form.email': {
      handler: function() {
        if (this.form.email.length !== 0) {
          this.error.email = false
        } else if (this.form.email.length === 0) {
          this.error.email = true
        }
      },
      deep: true
    },

    /** Controle de erro de nome */
    'form.nome': {
      handler: function() {
        if (this.form.nome.length !== 0) {
          this.error.nome = false
        } else if (this.form.nome.length === 0) {
          this.error.nome = true
        }
      },
      deep: true
    },

    /** Controle de erro de grupos */
    'form.grupos': {
      handler: function() {
        if (this.form.grupos.length !== 0) {
          this.error.grupos = false
        } else if (this.form.grupos.length === 0) {
          this.error.grupos = true
        }
      },
      deep: true
    }
  },
  computed: {
    disableSaveButton() {
      if (
        this.form.email !== '' &&
        this.form.login !== '' &&
        this.form.nome !== '' &&
        this.form.grupos.length !== 0
      ) {
        return false
      }
      return true
    }
  },
  async created() {
    /** Se for edição, pegar o ID so usuário */
    this.usersId = this.$route.params.id || null
    /** Get de métodos */
    await this.getHotels()
    await this.getUsersGroups()

    /** Verifica se é página de edição para retorno de dados do usuário */
    await this.pageType()

    if (this.routeType) {
      /** Retorna o ddi +55 por padrão durante a criação */
      const padraoPais = this.ddiOptions.find(
        (country) => country.text === 'Brazil (+55)'
      )
      this.form.telefoneDDI = padraoPais
      this.form.celularDDI = padraoPais
    }
  },
  methods: {
    /** Alterar senha */
    changeUserPassword() {
      this.$user
        .changeUserPassword(this.form.login)
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'E-mail enviado para redefinição de senha com sucesso.'
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao socilitar criação ou alteração da senha.'
          })
        })
    },

    /** Reativar usuário */
    reactivateUser() {
      this.$user
        .reactivateUser(this.userId)
        .then(() => {
          this.getSingleUser()
          this.$bus.$emit('show-alert-chip', {
            message: 'Usuário reativado com sucesso'
          })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao reativar usuário'
          })
        })
    },

    /** Verifica se é edição ou criação */
    pageType() {
      if (this.$route.name === 'UsuarioCriar') {
        this.routeType = true
        this.pageTitle = 'Criar usuário'
      } else {
        this.routeType = false
        this.getSingleUser()
      }
    },

    /** verificar se dado existe */
    ifExists(element) {
      /** se for array */
      let result = null
      if (element && typeof element.value !== 'undefined') {
        result = element.value
        /** se for string */
      } else if (element !== '') {
        result = element
      }
      return result === true ? 1 : result
    },

    /** Retorna dados do usuário */
    getSingleUser() {
      this.$user.getSingleUser(this.$route.params.id).then((response) => {
        const userInfo = response.data
        this.form = userInfo

        this.ifExists(
          userInfo.revendedorLimiteHorasCentralReservas !== null
            ? (this.form.revendedorLimiteHorasCentralReservas =
                userInfo.revendedorLimiteHorasCentralReservas)
            : (this.form.revendedorLimiteHorasCentralReservas = null)
        )

        this.userHours = this.form.revendedorLimiteHorasCentralReservas

        /** Retorno de datas */
        this.ifExists(
          userInfo.dataCadastro !== null
            ? (this.form.dataCadastro = formatDateHour(
                parseISO(userInfo.dataCadastro)
              ))
            : null
        )
        this.ifExists(
          userInfo.dataAlteracao !== null
            ? (this.form.dataAlteracao = formatDateHour(
                parseISO(userInfo.dataAlteracao)
              ))
            : null
        )
        this.ifExists(
          userInfo.dataExpiracao !== null
            ? (this.form.dataExpiracao = formatDateHour(
                parseISO(userInfo.dataExpiracao)
              ))
            : null
        )

        /** Retorna o ddi +55 por padrão durante a criação caso não exista um cadastrado */
        if (this.form.celular === null) {
          const padraoPais = this.ddiOptions.find(
            (country) => country.text === 'Brazil (+55)'
          )
          this.form.celularDDI = padraoPais
        } else {
          const padraoPais = this.ddiOptions.find(
            (country) => country.value === this.form.celular.ddi
          )
          this.form.celularDDI = padraoPais
          this.celular.ddd = this.form.celular.ddd
          this.celular.numero = this.form.celular.numero
        }

        /** Retorna o ddi +55 por padrão durante a criação caso não exista um cadastrado */
        if (userInfo.telefoneCentralReservas === null) {
          const padraoPais = this.ddiOptions.find(
            (country) => country.text === 'Brazil (+55)'
          )
          this.form.telefoneDDI = padraoPais
        } else {
          const padraoPais = this.ddiOptions.find(
            (country) => country.value === this.form.telefoneCentralReservas.ddi
          )
          this.form.telefoneDDI = padraoPais
          this.telefoneCentralReservas.ddd = this.form.telefoneCentralReservas.ddd
          this.telefoneCentralReservas.numero = this.form.telefoneCentralReservas.numero
        }

        /** Armazena o Id do usuário */
        this.userId = userInfo.codigo

        /** Tratamendo de dados de retorno de hoteis */
        const userHoteis = this.ifExists(
          userInfo.hoteis.length
            ? userInfo.hoteis.map((item) => {
                return item
              })
            : null
        )
        this.form.hoteis = userHoteis.map((item) => ({
          text: item.nome,
          value: item.codigo
        }))

        /** Tratamendo de dados de retorno de grupos */
        const userGroups = this.ifExists(
          userInfo.grupos.length
            ? userInfo.grupos.map((item) => {
                return item
              })
            : null
        )
        this.form.grupos = userGroups.map((item) => ({
          text: item.nome,
          value: item.codigo
        }))

        this.breadcrumb = [
          {
            label: 'Configurações'
          },
          {
            label: 'Usuários',
            url: '/admin/v2/usuarios'
          },
          {
            label: `Editando ${this.form.nome}`
          }
        ]
        this.pageTitle = 'Editando ' + this.form.nome
      })
    },

    /** Edição de usuário */
    editUser() {
      if (
        this.form.grupos.length === null ||
        this.form.login.length === null ||
        this.form.nome.length === null ||
        this.form.email.length === null
      ) {
        return this.$bus.$emit('show-alert-chip', {
          message: 'Por favor, preencha todos os campos obrigatórios'
        })
      }

      /** Tratamendo de dados para envio de Telefone */
      const celular = this.ifExists(
        this.celular.numero !== '' &&
          this.celular.ddd !== '' &&
          this.celular.numero !== null &&
          this.celular.ddd !== null
          ? {
              ddi: this.form.celularDDI.value,
              ddd: this.celular.ddd,
              numero: this.celular.numero
            }
          : null
      )

      /** Tratamendo de dados para envio de Telefone */
      const telefone = this.ifExists(
        this.telefoneCentralReservas.numero !== '' &&
          this.telefoneCentralReservas.ddd !== '' &&
          this.telefoneCentralReservas.numero !== null &&
          this.telefoneCentralReservas.ddd !== null
          ? {
              ddi: this.form.telefoneDDI.value,
              ddd: this.telefoneCentralReservas.ddd,
              numero: this.telefoneCentralReservas.numero
            }
          : null
      )

      /** Tratamendo de dados para envio de Hoteis */
      let HoteisIDs = null
      if (this.form.hoteis.length === 0) {
        HoteisIDs = this.hoteisOptions.map((item) => {
          return parseInt(item.value)
        })
      } else {
        HoteisIDs = this.form.hoteis.map((item) => {
          return parseInt(item.value)
        })
      }

      /** Tratamendo de dados para envio de Grupos */
      let GruposIDs = null
      if (this.form.grupos.length > 1) {
        GruposIDs = this.form.grupos.map((item) => {
          return parseInt(item.value)
        })
      } else {
        GruposIDs = [this.form.grupos[0].value]
      }

      this.$user
        .editUser({
          codigo: parseInt(this.$route.params.id),
          login: this.form.login,
          nome: this.form.nome,
          email: this.form.email,
          celular: celular,
          telefoneCentralReservas: telefone,
          revendedor: this.form.revendedor,
          revendedorCNPJ: this.form.revendedorCNPJ,
          revendedorSource: this.form.revendedorSource,
          revendedorLimiteHorasCentralReservas: parseInt(
            this.form.revendedorLimiteHorasCentralReservas
          ),
          hoteis: HoteisIDs,
          grupos: GruposIDs,
          status: this.form.status
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Usuário editado com sucesso'
          })
          this.$router.push({ name: 'UsuarioLista' })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message: err.messages[0]
          })
        })
    },

    /** Cria uma nova receita */
    createUser() {
      if (
        this.form.grupos.length === 0 ||
        this.form.login === null ||
        this.form.nome === null ||
        this.form.email === null
      ) {
        return this.$bus.$emit('show-alert-chip', {
          message: 'Por favor, preencha todos os campos obrigatórios'
        })
      }

      /** Tratamendo de dados para envio de Telefone */
      const celular = this.ifExists(
        this.celular.numero !== '' &&
          this.celular.ddd !== '' &&
          this.celular.numero !== null &&
          this.celular.ddd !== null
          ? {
              ddi: this.form.celularDDI.value,
              ddd: this.celular.ddd,
              numero: this.celular.numero
            }
          : null
      )

      /** Tratamendo de dados para envio de Telefone */
      const telefone = this.ifExists(
        this.telefoneCentralReservas.numero !== '' &&
          this.telefoneCentralReservas.ddd !== '' &&
          this.telefoneCentralReservas.numero !== null &&
          this.telefoneCentralReservas.ddd !== null
          ? {
              ddi: this.form.telefoneDDI.value,
              ddd: this.telefoneCentralReservas.ddd,
              numero: this.telefoneCentralReservas.numero
            }
          : null
      )

      /** Tratamendo de dados para envio de CNPJ */
      const cnpj = this.ifExists(
        this.form.revendedorCNPJ !== null
          ? this.form.revendedorCNPJ.replace(/\D/g, '')
          : null
      )

      /** Tratamendo de dados para envio de Hoteis */
      let HoteisIDs = null
      if (this.form.hoteis.length === 0) {
        HoteisIDs = this.hoteisOptions.map((item) => {
          return parseInt(item.value)
        })
      } else {
        HoteisIDs = this.form.hoteis.map((item) => {
          return parseInt(item.value)
        })
      }

      /** Tratamendo de dados para envio de Grupos */
      let GruposIDs = null
      if (this.form.grupos.length > 1) {
        GruposIDs = this.form.grupos.map((item) => {
          return parseInt(item.value)
        })
      } else {
        GruposIDs = [this.form.grupos[0].value]
      }

      this.$user
        .createUser({
          login: this.form.login,
          nome: this.form.nome,
          email: this.form.email,
          celular: celular,
          telefoneCentralReservas: telefone,
          revendedor: this.form.revendedor,
          revendedorCNPJ: cnpj,
          revendedorSource: this.form.revendedorSource,
          revendedorLimiteHorasCentralReservas: parseInt(
            this.form.revendedorLimiteHorasCentralReservas
          ),
          hoteis: HoteisIDs,
          status: true,
          grupos: GruposIDs || [GruposIDs]
        })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Usuário criado com sucesso'
          })
          this.$router.push({ name: 'UsuarioLista' })
        })
        .catch((err) => {
          this.$bus.$emit('show-alert-chip', {
            message: err.messages[0]
          })
        })
    },

    /** Busca os grupos  */
    getUsersGroups() {
      this.$user.getUsersGroups().then((response) => {
        const arrGrupos = response.data
        this.mapUsersGroupsFilter(arrGrupos)
      })
    },

    /** Cria o objeto de checkbox para os grupos  */
    mapUsersGroupsFilter(arrGrupos) {
      this.gruposOptions = arrGrupos.map((grupo) => {
        return {
          text: grupo.nome,
          value: grupo.codigo
        }
      })
    },

    /** Busca os hoteis  */
    getHotels() {
      this.$initialSetting.getHotels().then((response) => {
        const arrayHotels = response.data
        this.mapHotelsFilter(arrayHotels)
      })
    },

    /** Cria o objeto de checkbox para os hoteis  */
    mapHotelsFilter(arrHotels) {
      this.hoteisOptions = arrHotels.map((hotel) => {
        return {
          text: hotel.nome,
          value: hotel.codigo
        }
      })
    },

    /** Metodo de modificar checkbox */
    formatCheckbox() {
      if (this.form.revendedor === false) {
        this.form.revendedor = false
        this.form.revendedorCNPJ = null
        this.form.revendedorSource = null
        this.form.revendedorLimiteHorasCentralReservas = null
      } else if (this.form.revendedor === true) {
        this.form.revendedor = true
        if (this.userHours === null) {
          this.form.revendedorLimiteHorasCentralReservas = 48
        } else {
          this.form.revendedorLimiteHorasCentralReservas = this.userHours
        }
      }
    },

    /** Chamada para deletar usuário */
    disableUser() {
      this.$user
        .disableUser({ usuarios: [this.userDelete] })
        .then(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Usuário excluido com sucesso'
          })
          this.$router.push({ name: 'UsuarioLista' })
        })
        .catch(() => {
          this.$bus.$emit('show-alert-chip', {
            message: 'Erro ao desativar usuário'
          })
        })
    },

    /** Controle de deleção */
    deleteModal(userId) {
      this.showDeleteModal = true
      this.userDelete = userId
    },

    /** Controle de cancelamento de deleção */
    cancelDisable() {
      this.showDeleteModal = false
      this.userDelete = null
    },
    /** Helper de data */
    formatDateHour: formatDateHour
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .lb-breadcrumb--link:nth-child(2) {
    cursor: default;
  }

  .pmw-label.pmw-label__placeholder {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
.large {
  width: 270px;
}
.medium {
  width: 150px;
}
.small {
  width: 56px;
}
.sidebar-title {
  margin-top: 40px;
  margin-left: 20px;
  margin-bottom: 15px;
}
.sidebar-buttons {
  width: 215px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: auto;
}
.horizontal-line {
  margin: 45px auto 45px;
}
.text-input {
  &--email {
    margin-bottom: 30px;
    margin-top: 37px;
  }
  &--limit {
    width: 141px;
  }
}
.form {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
}
.pmw-form-field {
  ::v-deep {
    /** Alterar as cores novas do form-input */
    .pmw-input {
      background-color: $white;
    }
    .pmw-input__focus {
      border-color: pmw-colors('blue-100');
    }
    .pmw-input--field {
      &:focus {
        border-color: pmw-colors('blue-100');
      }
    }
    /** Alterar as cores novas do form-select */
    .pmw-select--button__caret-sort-up,
    .pmw-select--button__show {
      border-bottom-color: pmw-colors('blue-100');
    }
    .pmw-select--button:focus,
    .search-input__focus {
      border-color: pmw-colors('blue-100');
      border-bottom-color: pmw-colors('blue-100');
    }
    .pmw-select-options--item__hover:hover,
    .pmw-select-options--item__checked {
      background-color: pmw-colors('blue-100') !important;
    }
  }
}

.paragraph {
  &--phone {
    color: $blue;
    align-self: end;
    line-height: 18px;
    margin-left: 30px;
  }
  &--password {
    color: $gray-dark;
    margin-bottom: 30px;
  }
}
.checkbox-input {
  margin-top: 40px;
  margin-bottom: 20px;
  width: 22.5%;
}
.select {
  width: 340px;

  &--small {
    width: 55px;
  }
  &--medium {
    width: 150px;
  }
}
.toggle-title {
  display: flex;
  gap: 7px;
  margin: 30px 0 20px;
  align-items: center;
  &--button {
    border: none;
    background: inherit;
    padding: inherit;
    color: $gray-darkest;
  }
  &--icon {
    transform: rotate(-270deg);
    width: 8.25px;
    height: 8.25px;
  }
}
.toggle-content {
  display: flex;
  gap: 55px;
  color: pmw-colors('grey-90');
  &--title {
    margin-bottom: 15px;
  }
}
.bottom-info {
  display: flex;
  gap: 55px;
  color: pmw-colors('grey-90');
  &--title {
    margin-bottom: 10px;
  }
  &--date {
    margin-top: 20px;
  }
  &--expired {
    color: pmw-colors('grey-90');
    margin-bottom: 10px;
  }
  &--link {
    color: $blue;
    width: 160px;
    height: 30px;
    cursor: pointer;
  }
}
.bottom-buttons {
  display: flex;
  justify-content: space-between;
  &--return,
  &--save {
    width: 215px;
  }
}
/* Input */
input.form-control {
  border-radius: 0;
  border-left: 0 none;
  border-right: 0 none;
  border-top: 0 none;
  border-bottom: 1px solid pmw-colors('grey-90');
  padding-left: 0;
  color: pmw-colors('grey-90');
  font-size: 14px;
  font-weight: 400;
  &:focus {
    border-bottom-color: pmw-colors('blue-100');
    border-bottom: 1px solid pmw-colors('blue-100');
    box-shadow: none;
    border-bottom-width: 2px;
  }
  &.input-transparent {
    border-bottom-color: $white;
    background-color: transparent;
    color: $white;
  }
  &.is-invalid {
    border-width: 2px;
    border-color: pmw-colors('error-100');
    &:focus {
      box-shadow: none;
    }
  }
  @include placeholder {
    color: #cdcdcd;
  }
}
.input-icon {
  position: relative;
  input {
    padding-right: 26px;
  }
  .icon {
    font-size: 25px;
    position: absolute;
    right: 0px;
    bottom: 6px;
  }
  input:focus + .icon {
    color: pmw-colors('blue-100');
  }
  input.input-transparent:focus + .icon {
    color: $white;
  }
}
.form-field {
  display: inline-block;
  position: relative;

  &--label {
    display: inline-flex;
    align-items: center;
    margin: 0;
    font-size: pmw-font-size('size-medium');
    font-weight: pmw-font-weight('weight-medium');
  }
}
</style>
